import React from 'react';
import { Dialog, DialogBody, DialogFooter, Spinner } from '@blueprintjs/core';
import type { BatchStatus, JobState, StageError } from './api';

/* ── Formatting ───────────────────────────────────────── */

export const fmtInt = (n: number | null | undefined) => (n == null ? '-' : Number(n).toLocaleString('en-US'));
export const fmtMoney = (n: number | null | undefined) =>
  n == null ? '-' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
/** Batch dates are calendar days (YYYY-MM-DD). Never route them through Date. */
export const fmtDay = (day: string | null | undefined) => {
  if (!day) return '-';
  const [y, m, d] = day.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
};
export const fmtWhen = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso)) : '-';
export const fmtBytes = (n: number | null | undefined) =>
  n == null ? '-' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

/** Starting zoom for the Imports sheets and tables: lead files are wide, so
 * they open small enough to show more columns. Each table remembers changes. */
export const LEAD_IMPORT_ZOOM = 0.7;

/* ── Status ───────────────────────────────────────────── */

const STATUS: Record<BatchStatus, { label: string; tone: string }> = {
  Inspecting: { label: 'Reading file', tone: 'progress' },
  Draft: { label: 'Needs setup', tone: 'neutral' },
  Preparing: { label: 'Building preview', tone: 'progress' },
  Previewed: { label: 'Ready to import', tone: 'info' },
  Committing: { label: 'Importing', tone: 'progress' },
  Committed: { label: 'Imported', tone: 'success' },
  Failed: { label: 'Needs attention', tone: 'danger' },
  Cancelled: { label: 'Stopped', tone: 'muted' },
};

export function statusLabel(status: BatchStatus): string {
  return STATUS[status]?.label ?? status;
}

export function StatusPill({ status }: { status: BatchStatus }) {
  const s = STATUS[status] ?? { label: status, tone: 'neutral' };
  return <span className={`li-pill li-pill--${s.tone}`}>{s.tone === 'progress' && <span className="li-pill__pulse" aria-hidden />}{s.label}</span>;
}

export function Pill({ tone = 'neutral', children, title }: { tone?: string; children: React.ReactNode; title?: string }) {
  return <span className={`li-pill li-pill--${tone}`} title={title}>{children}</span>;
}

const STAGE_NAME: Record<string, string> = {
  inspect: 'Reading the file', prepare: 'Building the preview', commit: 'Importing', verify: 'Verifying',
  verify_phone: 'Verifying phones', verify_email: 'Verifying emails',
};

/* Reading a file: the phases the worker reports, in order. */
const INSPECT_STEPS = [
  { key: 'downloading', label: 'Download' },
  { key: 'reading', label: 'Read rows' },
  { key: 'saving', label: 'Save rows' },
  { key: 'checking', label: 'Check sheets' },
] as const;

function inspectDetail(p: Record<string, unknown>): string {
  const rows = fmtInt(Number(p.rows_read ?? 0));
  switch (p.stage) {
    case 'downloading': {
      const total = Number(p.bytes_total ?? 0);
      return total > 0 ? `Downloaded ${fmtBytes(Number(p.bytes_read ?? 0))} of ${fmtBytes(total)}` : `Downloaded ${fmtBytes(Number(p.bytes_read ?? 0))}`;
    }
    case 'reading': return `${rows} rows read from ${fmtBytes(Number(p.bytes_total ?? 0))}`;
    case 'saving': return `All ${rows} rows read. Saving them`;
    default: return `${rows} rows saved. Checking the sheets`;
  }
}

function InspectSteps({ stage }: { stage: string }) {
  const at = INSPECT_STEPS.findIndex((s) => s.key === stage);
  return (
    <ol className="li-steps" aria-label="Reading progress">
      {INSPECT_STEPS.map((s, i) => (
        <li key={s.key} className={`li-steps__item li-steps__item--${i < at ? 'done' : i === at ? 'current' : 'todo'}`} aria-current={i === at ? 'step' : undefined}>
          {s.label}
        </li>
      ))}
    </ol>
  );
}

/**
 * What the server is doing for this batch right now, from the durable job
 * row: running with progress, queued, waiting for a worker, stalled,
 * retrying after a temporary problem, or blocked/failed with the reason.
 * It never claims progress the server has not recorded.
 */
export function JobBanner({ job, stageError, onRetry, retrying, idleText }: {
  job: JobState | null; stageError: StageError | null; onRetry?: () => void; retrying?: boolean; idleText?: string;
}) {
  const active = job && (job.state === 'queued' || job.state === 'running');
  if (stageError && !(active && stageError.class === 'transient')) {
    const blocked = stageError.class === 'blocked';
    return (
      <div className={`li-banner ${blocked ? 'li-banner--warn' : 'li-banner--danger'}`} role="alert">
        <div className="li-banner__text">
          <b>{STAGE_NAME[stageError.stage] ?? 'This step'} {blocked ? 'is blocked' : 'did not finish'}.</b>{' '}
          {stageError.message}
          {blocked && ' This needs a fix before retrying.'}
        </div>
        {onRetry && <button type="button" className="li-btn li-btn--secondary" disabled={retrying} onClick={onRetry}>{retrying ? 'Retrying…' : 'Try again'}</button>}
      </div>
    );
  }
  if (!active || !job) return idleText ? <div className="li-banner li-banner--muted">{idleText}</div> : null;
  const p = job.progress ?? {};
  const reading = job.kind === 'inspect' && job.state === 'running' && !job.stalled && INSPECT_STEPS.some((s) => s.key === p.stage);
  const done = Number(reading ? p.bytes_read ?? 0 : p.commit_done ?? 0);
  const total = Number(reading ? (p.stage === 'downloading' ? p.bytes_total ?? 0 : 0) : p.commit_total ?? 0);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null;
  let detail: string;
  if (job.stalled) detail = 'No progress reported for a few minutes. It will be picked up again automatically.';
  else if (job.state === 'queued' && job.error_code === 'retry_scheduled') detail = `Some checks got a temporary provider error. They are retried automatically, next at ${fmtWhen(job.next_attempt_at)}.`;
  else if (job.state === 'queued' && job.error_code === 'awaiting_unknown_outcomes') detail = `Waiting for provider answers that did not arrive. Checks still without an answer at ${fmtWhen(job.next_attempt_at)} are marked unknown and not sent again.`;
  else if (job.state === 'queued' && stageError?.class === 'transient') detail = `A temporary problem interrupted it (${stageError.message}). Retrying automatically.`;
  else if (job.state === 'queued') {
    // Uploads, phone checks and email checks each have their own loop taking one job at a time per
    // instance; ahead counts only jobs of this job's loop.
    const ahead = job.ahead ?? 0;
    const what = job.kind === 'verify_phone' ? 'phone check' : job.kind.startsWith('verify') ? 'email check' : 'import';
    if (ahead > 0) detail = `Queued behind ${fmtInt(ahead)} other ${what} job${ahead === 1 ? '' : 's'}. It starts when the service is free.`;
    else if (job.waiting_for_worker) detail = 'Nothing is ahead of it, but the import service has not picked it up for a few minutes. It may be restarting; this starts on its own when it is back.';
    else detail = 'Queued. It starts in a few seconds.';
  }
  else if (reading) detail = inspectDetail(p);
  else if (pct != null) detail = `${fmtInt(done)} of ${fmtInt(total)} phones (${pct}%)`;
  else detail = 'Working';
  return (
    <div className={`li-banner ${job.stalled ? 'li-banner--warn' : 'li-banner--progress'}`} aria-live="polite">
      {!job.stalled && <Spinner size={16} />}
      <div className="li-banner__text">
        <b>{STAGE_NAME[job.kind]}.</b> {detail}
        {reading && <InspectSteps stage={String(p.stage)} />}
        {pct != null && <div className="li-progress"><div className="li-progress__bar" style={{ width: `${pct}%` }} /></div>}
        <div className="li-banner__sub">You can leave this page. The work continues on the server.</div>
      </div>
    </div>
  );
}

/* ── Row ledger: how the file's rows became leads ─────── */

export type LedgerPart = 'rows' | 'leads' | 'merged' | 'skipped';

/** With onOpen, each part with rows is a button that opens those rows. */
export function RowLedger({ rows, leads, merged, skipped, compact = false, onOpen, openable = ['rows', 'leads', 'merged', 'skipped'] }: {
  rows: number; leads: number; merged: number; skipped: number; compact?: boolean;
  onOpen?: (part: LedgerPart) => void; openable?: LedgerPart[];
}) {
  const total = Math.max(rows, 1);
  const seg = (n: number) => `${Math.max((n / total) * 100, n > 0 ? 1.5 : 0)}%`;
  const ok = rows === leads + merged + skipped;
  const part = (key: LedgerPart, n: number, label: string, className = '') => {
    const text = <><b>{fmtInt(n)}</b> {label}</>;
    if (!onOpen || n === 0 || !openable.includes(key)) return <span className={className}>{text}</span>;
    return <button type="button" className={`li-ledger__open ${className}`} onClick={() => onOpen(key)}>{text}</button>;
  };
  return (
    <figure className={`li-ledger${compact ? ' li-ledger--compact' : ''}`} aria-label="How the rows in the file became leads">
      <div className="li-ledger__bar" role="img"
        aria-label={`${fmtInt(rows)} data rows: ${fmtInt(leads)} leads, ${fmtInt(merged)} merged repeats, ${fmtInt(skipped)} skipped without a usable phone`}>
        <span className="li-ledger__seg li-ledger__seg--leads" style={{ width: seg(leads) }} />
        <span className="li-ledger__seg li-ledger__seg--merged" style={{ width: seg(merged) }} />
        <span className="li-ledger__seg li-ledger__seg--skipped" style={{ width: seg(skipped) }} />
      </div>
      <figcaption className="li-ledger__legend">
        {part('rows', rows, 'rows in the file')}
        {part('leads', leads, 'leads', 'li-ledger__key li-ledger__key--leads')}
        {part('merged', merged, 'merged repeats', 'li-ledger__key li-ledger__key--merged')}
        {part('skipped', skipped, 'skipped, no usable phone', 'li-ledger__key li-ledger__key--skipped')}
        {!ok && <span className="li-ledger__warn">Counts do not add up</span>}
      </figcaption>
    </figure>
  );
}

/* ── Sortable tables ──────────────────────────────────── */

export interface SortState<K extends string> { key: K; desc: boolean }

/**
 * Client-side sorting for the small li-table lists. Blanks always sort last;
 * text compares naturally ("Batch 9" before "Batch 10").
 */
export function useSorted<R, K extends string>(
  rows: R[], value: (row: R, key: K) => string | number | null | undefined, initial: SortState<K>,
) {
  const [sort, setSort] = React.useState<SortState<K>>(initial);
  const sorted = React.useMemo(() => {
    const dir = sort.desc ? -1 : 1;
    return rows.map((row, i) => ({ row, i, v: value(row, sort.key) })).sort((a, b) => {
      const blankA = a.v == null || a.v === '';
      const blankB = b.v == null || b.v === '';
      if (blankA || blankB) return blankA === blankB ? a.i - b.i : blankA ? 1 : -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number'
        ? a.v - b.v
        : String(a.v).localeCompare(String(b.v), 'en', { numeric: true, sensitivity: 'base' });
      return c * dir || a.i - b.i;
    }).map((x) => x.row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  const toggle = (key: K, numeric = false) =>
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: numeric }));
  return { sorted, sort, toggle };
}

/** A column header that sorts its table. Numbers and dates start descending. */
export function SortTh<K extends string>({ k, sort, onSort, numeric = false, children, className = '' }: {
  k: K; sort: SortState<K>; onSort: (key: K, numeric: boolean) => void; numeric?: boolean; children: React.ReactNode; className?: string;
}) {
  const on = sort.key === k;
  return (
    <th className={`${className} li-sortth${on ? ' is-on' : ''}`} aria-sort={on ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
      <button type="button" onClick={() => onSort(k, numeric)}>
        {children}<span className="li-sortth__mark" aria-hidden>{on ? (sort.desc ? '↓' : '↑') : '↕'}</span>
      </button>
    </th>
  );
}

/* ── Layout primitives ────────────────────────────────── */

export function Card({ title, actions, children, className = '', flush = false, subtitle }: {
  title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string; flush?: boolean; subtitle?: React.ReactNode;
}) {
  return (
    <section className={`li-card ${className}`}>
      {(title || actions) && (
        <header className="li-card__head">
          <div>
            {title && <h3 className="li-card__title">{title}</h3>}
            {subtitle && <p className="li-card__sub">{subtitle}</p>}
          </div>
          {actions && <div className="li-card__actions">{actions}</div>}
        </header>
      )}
      <div className={flush ? '' : 'li-card__body'}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, sub, tone }: { label: React.ReactNode; value: React.ReactNode; sub?: React.ReactNode; tone?: string }) {
  return (
    <div className={`li-stat${tone ? ` li-stat--${tone}` : ''}`}>
      <div className="li-stat__value">{value}</div>
      <div className="li-stat__label">{label}</div>
      {sub && <div className="li-stat__sub">{sub}</div>}
    </div>
  );
}

export function Empty({ title, children, action }: { title?: string; children?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="li-empty">
      {title && <div className="li-empty__title">{title}</div>}
      {children && <div className="li-empty__text">{children}</div>}
      {action}
    </div>
  );
}

export interface ConfirmOptions { title: string; body?: React.ReactNode; action: string; danger?: boolean }

/** In-app confirmation in place of window.confirm. `confirm(...)` resolves
 * true on the action and false on cancel; render `dialog` once. */
export function useConfirm(): [(o: ConfirmOptions) => Promise<boolean>, React.ReactNode] {
  const [state, setState] = React.useState<{ open: boolean; opts: ConfirmOptions; resolve: (ok: boolean) => void } | null>(null);
  const confirm = React.useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ open: true, opts, resolve })), []);
  // Keep the options while the dialog animates out so its text does not blank.
  const close = (ok: boolean) => { state?.resolve(ok); setState((s) => (s ? { ...s, open: false } : s)); };
  const opts = state?.opts;
  const dialog = (
    <Dialog isOpen={!!state?.open} onClose={() => close(false)} title={opts?.title} className="li-confirm">
      {opts?.body && <DialogBody>{opts.body}</DialogBody>}
      <DialogFooter actions={<>
        <button type="button" className="li-btn li-btn--quiet" onClick={() => close(false)}>Cancel</button>
        <button type="button" autoFocus className={`li-btn ${opts?.danger ? 'li-btn--danger' : 'li-btn--primary'}`} onClick={() => close(true)}>{opts?.action}</button>
      </>} />
    </Dialog>
  );
  return [confirm, dialog];
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <div className="li-inline-error" role="alert">{message}</div>;
}

/** Segmented single choice. `value` may be undefined: nothing is
 * preselected where the business default is still open. */
export function Segmented<T extends string>({ options, value, onChange, ariaLabel, disabled, size = 'md' }: {
  options: { value: T; label: React.ReactNode; hint?: string }[];
  value: T | undefined;
  onChange: (v: T) => void;
  ariaLabel: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
}) {
  return (
    <div className={`li-seg li-seg--${size}`} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.hint}
          disabled={disabled}
          className={`li-seg__opt${value === o.value ? ' is-on' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const REASON_LABEL: Record<string, string> = {
  existing_merchant: 'Already a merchant',
  deal_history: 'Has deal history',
  dnd: 'Do not disturb',
  held: 'Needs review',
  batch_not_committed: 'Import not finished',
};

/* ── Verification results ─────────────────────────────── */

/** Provider results as stored by the worker: Veriphone gives the phone type
 * or "invalid", MillionVerifier gives ok, catch_all, unknown, disposable or
 * invalid. */
export const VERIFY_LABEL: Record<string, { label: string; tone: string }> = {
  ok: { label: 'Valid', tone: 'success' },
  mobile: { label: 'Mobile', tone: 'success' },
  fixed_line: { label: 'Landline', tone: 'success' },
  fixed_line_or_mobile: { label: 'Landline or mobile', tone: 'success' },
  voip: { label: 'VoIP', tone: 'info' },
  toll_free: { label: 'Toll free', tone: 'info' },
  catch_all: { label: 'Accepts all', tone: 'warn' },
  disposable: { label: 'Disposable', tone: 'warn' },
  unknown: { label: 'Unknown', tone: 'muted' },
  invalid: { label: 'Invalid', tone: 'danger' },
};

export function VerifyCell({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="li-muted">-</span>;
  const v = VERIFY_LABEL[status] ?? { label: status.replace(/_/g, ' '), tone: 'neutral' };
  return <Pill tone={v.tone}>{v.label}</Pill>;
}

/** Spreadsheet column letter for a zero-based position: 0 is A, 26 is AA. */
export function colLetter(i: number): string {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}
